import { avatarPalettes } from '@soteria/design-system-ve';
import type { AvatarThemeKey } from '@soteria/design-system-ve';

/**
 * Extracts initials from a name string
 * @param name - Full name (e.g., "Warren Lovell")
 * @returns Uppercase initials (e.g., "WL"), max 2 characters
 */
export function getInitials(name?: string | null): string {
  if (!name || name.trim() === '') {
    return '?';
  }

  const words = name.trim().split(/\s+/);

  if (words.length === 1) {
    // Single name: take first two characters or just first if only one char
    return words[0].substring(0, 2).toUpperCase();
  }

  // Multiple words: take first letter of first and last word
  const firstInitial = words[0].charAt(0);
  const lastInitial = words[words.length - 1].charAt(0);

  return (firstInitial + lastInitial).toUpperCase();
}

/**
 * Returns the appropriate avatar palette for the current theme.
 * @param themeKey - The theme key (dark, light, hc-dark, hc-light)
 * @returns Array of WCAG-compliant hex color strings for avatar backgrounds
 */
export function getAvatarPalette(themeKey: AvatarThemeKey): readonly string[] {
  return avatarPalettes[themeKey];
}

/**
 * Resolves the theme key from useTheme() state values.
 * @param isDarkTheme - Whether the current theme is dark
 * @param isHighContrast - Whether high contrast mode is enabled
 * @returns The avatar theme key for palette selection
 */
export function resolveAvatarThemeKey(isDarkTheme: boolean, isHighContrast: boolean): AvatarThemeKey {
  if (isHighContrast) return isDarkTheme ? 'hc-dark' : 'hc-light';

  return isDarkTheme ? 'dark' : 'light';
}

// Re-export for consumers
export { getAvatarColor } from '@soteria/design-system-ve';
export type { AvatarThemeKey } from '@soteria/design-system-ve';
